import { useAuth } from '@/app/context';
import {
  AuthHeader,
  ErrorMessage,
  LoginForm,
} from '@/features/auth/components';
import { getPostAuthRedirectPath } from '@/features/auth/utils/get-post-auth-redirect-path.util';
import { LoadingSpinner } from '@/shared/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
  pendingComponent: LoadingSpinner,
});

function LoginPage() {
  const [error, setError] = useState('');
  const { signInFirstStep, isAuthenticated, attributes, getCurrentUserValues } =
    useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && attributes) {
      navigate({ to: getPostAuthRedirectPath(attributes) });
    }
  }, [attributes, isAuthenticated, navigate]);

  const handleSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setError('');
    const result = await signInFirstStep(email, password);

    // Login bem-sucedido sem challenges (ex: pool sem MFA).
    // Amplify retorna { isSignedIn: true, nextStep.signInStep: 'DONE' }.
    if (
      result &&
      typeof result === 'object' &&
      'isSignedIn' in result &&
      result.isSignedIn === true
    ) {
      const userCurrentValues = await getCurrentUserValues();
      navigate({
        to: getPostAuthRedirectPath(userCurrentValues?.attributes),
        viewTransition: {
          types: ['slide-left'],
        },
      });
      return;
    }

    if (
      result &&
      typeof result === 'object' &&
      result.nextStep &&
      typeof result.nextStep === 'object' &&
      'signInStep' in result.nextStep &&
      result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
    ) {
      navigate({
        to: '/two-factor',
        viewTransition: {
          types: ['slide-left'],
        },
      });
    } else if (
      result &&
      typeof result === 'object' &&
      result.nextStep &&
      typeof result.nextStep === 'object' &&
      'signInStep' in result.nextStep &&
      result.nextStep.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      navigate({
        to: '/reset',
        viewTransition: {
          types: ['slide-left'],
        },
      });
    } else {
      setError(
        'Credenciais inválidas.\nVerifique seus dados e tente novamente.'
      );
    }
  };

  return (
    <>
      <AuthHeader
        title='Bem-vindo(a) ao Portal de Assessoria.'
        subtitle='Acesse com suas credenciais para continuar.'
      />

      <LoginForm onSubmit={handleSubmit} />

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
    </>
  );
}
